
import React from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QuestionResponse } from "./QuestionResponse";
import { Question } from "@/types/test";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { QuestionTimer } from "./QuestionTimer";
import { useIsMobile } from "@/hooks/use-mobile";
import { VideoRecorderHandle } from "./media/VideoRecorder";
import { Loader2 } from "lucide-react";

interface TestContentProps {
  currentQuestion: Question;
  response: any;
  onResponseChange: (response: any) => void;
  testId?: string;
  testResponseId?: string;
  onNext: () => void;
  onTimerExpiry?: () => void;
  isLastQuestion: boolean;
  currentQuestionIndex: number;
  totalQuestions: number;
  testTitle: string;
  videoRecorderRef: React.RefObject<VideoRecorderHandle>;
  isSubmitting?: boolean;
  isMediaUploading?: boolean;
  onMediaUploadStateChange?: (isUploading: boolean) => void;
  onVideoRecordingStart?: () => void;
  onVideoRecordingStop?: () => void;
  onUploadProgress?: (questionId: string, status: 'pending' | 'complete' | 'failed', type: 'video' | 'audio') => void;
  isUploadInProgress?: boolean;
  isVideoRecorderReady?: boolean;
}

export function TestContent({ 
  currentQuestion, 
  response, 
  onResponseChange,
  testId,
  testResponseId,
  onNext,
  onTimerExpiry,
  isLastQuestion,
  currentQuestionIndex,
  totalQuestions,
  testTitle,
  videoRecorderRef,
  isSubmitting = false,
  isMediaUploading = false,
  onMediaUploadStateChange,
  onVideoRecordingStart,
  onVideoRecordingStop,
  onUploadProgress,
  isUploadInProgress = false,
  isVideoRecorderReady = true,
}: TestContentProps) {
  const isMobile = useIsMobile();

  const handleResponseChange = async (newResponse: any) => {
    onResponseChange(newResponse);
  };

  if (!currentQuestion) {
    return (
      <div className="container max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <Card className="w-full max-w-3xl mx-auto">
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-32 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progressPercentage = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  return (
    <div className={`container max-w-7xl mx-auto px-6 md:px-12 lg:px-24`}>
      <div className={`w-full ${isMobile ? 'max-w-full' : 'max-w-4xl'} mx-auto flex flex-col items-center`}>
        <div className="w-full mb-6">
          <div className="mb-3">
            <p className={`${isMobile ? 'text-base' : 'text-lg'} font-medium line-clamp-1`}>{testTitle}</p>
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
              <span>{progressPercentage.toFixed(0)}% complete</span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
          <QuestionTimer 
            timeLimit={currentQuestion.time_limit} 
            onTimeUp={onTimerExpiry || onNext}
            questionId={currentQuestion.id}
            isPaused={isUploadInProgress}
          />
        </div>
        
        <Card className="w-full shadow-md">
          <CardHeader className={`${isMobile ? 'py-3 px-4' : 'pb-2'}`}>
            <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold`}>
              Question {currentQuestionIndex + 1}
            </h2>
          </CardHeader>
          <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <QuestionResponse
              question={currentQuestion}
              response={response}
              onChange={handleResponseChange}
              testResponseId={testResponseId}
              ref={videoRecorderRef}
              onUploadStateChange={onMediaUploadStateChange}
              onVideoRecordingStart={onVideoRecordingStart}
              onVideoRecordingStop={onVideoRecordingStop}
              onUploadProgress={onUploadProgress}
              isUploadInProgress={isUploadInProgress}
              isVideoRecorderReady={isVideoRecorderReady}
            />
          </CardContent>
          <CardFooter className={`${isMobile ? 'px-4 py-3 flex justify-end' : 'px-6 py-4 flex justify-end'}`}>
            <Button 
              onClick={onNext} 
              size={isMobile ? "default" : "lg"}
              className={`${isMobile ? 'w-full' : ''}`}
              disabled={isSubmitting || isMediaUploading || isUploadInProgress}
            >
              {isUploadInProgress ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : isMediaUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isLastQuestion ? "Submitting..." : "Processing..."}
                </>
              ) : (
                isLastQuestion ? "Complete Test" : "Next Question"
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
